import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import EventsList from './components/EventsList';
import TeamsList from './components/TeamsList';
import CreateEvent from './components/CreateEvent';
import EditEvent from './components/EditEvent';
import CreateTeam from './components/CreateTeam';
import EventDetails from './components/EventDetails';
import TeamDetails from './components/TeamDetails';
import TeamDiscoveryPage from './components/TeamDiscoveryPage';
import EventRegistrationPage from './components/EventRegistrationPage';
import ParticipantList from './components/ParticipantList';
import Dashboard from './components/Dashboard';
import Home from './components/Home';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import JoinEvent from './components/JoinEvent';
import EventPresentationView from './components/EventPresentationView';
import AwsAccountManagement from './components/AwsAccountManagement';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { DashboardService } from './utils/dashboardService';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, user, logout, token } = useAuth();
  const [currentEventName, setCurrentEventName] = React.useState<string>('Event');
  const [userTeamInfo, setUserTeamInfo] = React.useState<{
    hasTeam: boolean;
    teamId: string | null;
    teamName: string | null;
    eventId: string | null;
  }>({
    hasTeam: false,
    teamId: null,
    teamName: null,
    eventId: null
  });

  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  // Fetch current event name and user team info for the navigation
  React.useEffect(() => {
    const fetchNavigationData = async () => {
      if (!token || !isAuthenticated) return;
      
      try {
        // Get enhanced dashboard status which includes user profile and team info from /users/me
        const enhancedStatus = await DashboardService.getEnhancedDashboardStatus(token);
        
        if (enhancedStatus.active_event_name) {
          setCurrentEventName(enhancedStatus.active_event_name);
        }
        
        // Update user team info from enhanced data
        const teamInfo = enhancedStatus.user_team_in_current_event_enhanced;
        setUserTeamInfo({
          hasTeam: teamInfo?.has_team || false,
          teamId: teamInfo?.team_id || null,
          teamName: teamInfo?.team_name || null,
          eventId: enhancedStatus.active_event_id
        });
        
        console.log('Navigation data updated from /users/me:', {
          eventName: enhancedStatus.active_event_name,
          activeEventId: enhancedStatus.active_event_id,
          teamInfo: teamInfo,
          totalTeams: enhancedStatus.user_profile?.current_teams?.length || 0,
          allUserTeams: enhancedStatus.user_profile?.current_teams?.map(t => ({
            teamId: t.team_id,
            eventId: t.event_id,
            name: t.name
          })) || [],
          finalUserTeamInfo: {
            hasTeam: teamInfo?.has_team || false,
            teamId: teamInfo?.team_id || null,
            teamName: teamInfo?.team_name || null,
            eventId: enhancedStatus.active_event_id
          }
        });
        
      } catch (error) {
        console.error('Enhanced dashboard status failed, falling back to basic methods:', error);
        
        // Fallback: try to get just the current event name
        try {
          const currentEvent = await DashboardService.getCurrentEvent(token);
          if (currentEvent?.name) {
            setCurrentEventName(currentEvent.name);
          }
        } catch (eventError) {
          console.debug('No current event found for navigation:', eventError);
          setCurrentEventName('Event'); // Reset to default
        }
        
        // Reset team info on error
        setUserTeamInfo({
          hasTeam: false,
          teamId: null,
          teamName: null,
          eventId: null
        });
      }
    };

    fetchNavigationData();
  }, [token, isAuthenticated]);

  if (!isAuthenticated) return null;

  return (
    <div className="header">
      <div className="container">
        <div className="header-layout">
          {/* Logo spanning full height */}
          <div className="header-logo-wrap">
            <img 
              src="/InnovationDayLogo2026.png" 
              alt="Creditsafe Innovation Day" 
              className="header-logo"
            />
          </div>
          
          {/* Middle section: Title and Navigation */}
          <div className="header-main">
            {/* Title row */}
            {/*
            <div>
              <h1 className="header-title">
                Creditsafe Innovation Days
              </h1>
            </div>
            */}
            
            {/* Navigation buttons row */}
            <nav className="header-nav">
              <Link 
                to="/" 
                className={`header-nav-link ${location.pathname === '/' ? 'active' : ''}`}
              >
                🏠 Home
              </Link>
              {/* <Link 
                to="/events" 
                className={location.pathname === '/events' ? 'active' : ''}
                style={{ color: 'white', textDecoration: 'none', padding: '4px 8px', borderRadius: '4px', transition: 'background-color 0.2s', fontSize: '0.9rem' }}
              >
                📅 Events
              </Link>
              <Link 
                to="/teams" 
                className={location.pathname === '/teams' ? 'active' : ''}
                style={{ color: 'white', textDecoration: 'none', padding: '4px 8px', borderRadius: '4px', transition: 'background-color 0.2s', fontSize: '0.9rem' }}
              >
                👥 Teams
              </Link>
              {isOrganizer && (
                <Link 
                  to="/create-event" 
                  className={location.pathname === '/create-event' ? 'active' : ''}
                  style={{ color: 'white', textDecoration: 'none', padding: '4px 8px', borderRadius: '4px', transition: 'background-color 0.2s', fontSize: '0.9rem' }}
                >
                  ➕ Create Event
                </Link>
              )}
              <Link 
                to="/create-team" 
                className={location.pathname === '/create-team' ? 'active' : ''}
                style={{ color: 'white', textDecoration: 'none', padding: '4px 8px', borderRadius: '4px', transition: 'background-color 0.2s', fontSize: '0.9rem' }}
              >
                🏗️ Create Team
              </Link> */}
              {/* If Organiser show Dashboard link */}
              {isOrganizer && (
                <Link 
                  to="/dashboard" 
                  className={`header-nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
                >
                  📊 Dashboard
                </Link>
              )}
              {userTeamInfo.hasTeam && userTeamInfo.teamId && userTeamInfo.eventId ? (
                <Link 
                  to={`/teams/${userTeamInfo.teamId}`}
                  className={`header-nav-link ${location.pathname === `/teams/${userTeamInfo.teamId}` ? 'active' : ''}`}
                >
                  ⭐ My Team ({userTeamInfo.teamName})
                </Link>
              ) : (
                <Link 
                  to="/join-event" 
                  className={`header-nav-link ${location.pathname === '/join-event' ? 'active' : ''}`}
                >
                  🚀 Join {currentEventName}
                </Link>
              )}
            </nav>
          </div>
          
          {/* Right section: User Info */}
          <div className="header-user-panel">
            <p className="header-welcome-text">
              Welcome, {user?.display_name || user?.email}
            </p>
            <button 
              onClick={logout} 
              className="btn header-logout-btn"
              style={{ fontSize: '0.8rem', padding: '6px 12px' }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationBanner: React.FC = () => {
  return (
    <div style={{
      backgroundColor: '#fff3cd',
      borderLeft: '4px solid #ffc107',
      padding: '12px 20px',
      margin: '0',
      marginTop: '-1px',
      fontSize: '0.9rem',
      color: '#856404',
      textAlign: 'center',
      borderBottom: '1px solid #ffeaa7',
      position: 'relative',
      zIndex: 1
    }}>
      ℹ️ If you encounter any errors with this site please reach out to Scott Murch, for general Innovation Day questions please use the{' '}
      <a 
        href="https://teams.microsoft.com/l/channel/19%3A10414f32a6ce47aaa54d81de6d4dbdeb%40thread.tacv2/Hackathon%20Support?groupId=82cb6564-f10b-4462-83d3-0df61fcb7279&tenantId=a8c3ac7a-1f5c-4b9e-a7e9-dec74e071af3"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#856404', textDecoration: 'underline' }}
      >
        Hackathon Support channel
      </a>
    </div>
  );
};

const Footer: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) return null;

  return (
    <footer style={{
      marginTop: '40px',
      padding: '20px 0',
      borderTop: '1px solid #e0e0e0',
      backgroundColor: '#f8f9fa',
      fontSize: '0.9rem'
    }}>
      <div className="container">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          {/* Links Section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '30px',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: '#666', fontWeight: '500' }}>🔗 Quick Links:</span>
            <a 
              href="https://dev.azure.com/creditsafe/Hackathon/_wiki/wikis/Hackathon.wiki" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#666', textDecoration: 'none' }}
            >
              📖 Hackathon Wiki
            </a>
            <a 
              href="https://teams.microsoft.com/l/channel/19%3AShGCnOeANAw36vBubyzz2ocwinIH-ws3WNNbVQ8TPOY1%40thread.tacv2/General?groupId=82cb6564-f10b-4462-83d3-0df61fcb7279&tenantId=a8c3ac7a-1f5c-4b9e-a7e9-dec74e071af3" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#666', textDecoration: 'none' }}
            >
              💬 Hackathon Teams Channel
            </a>
            <a 
              href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001'}/docs`}
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#666', textDecoration: 'none' }}
            >
              📚 API Documentation
            </a>
            <a 
              href="https://dev.azure.com/creditsafe/PlatformEngineering/_git/HackathonMgr" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#666', textDecoration: 'none' }}
            >
              💻 Source Code
            </a>
          </div>

          {/* App Info */}
          <div style={{ textAlign: 'right', color: '#666' }}>
            <div>Creditsafe Hackathon Manager</div>
            <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>
              Platform Engineering Team
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, login } = useAuth();

  // Wrapper function to handle Azure AD authentication success
  const handleAzureADSuccess = (user: any) => {
    console.log('Azure AD authentication success:', user);
    
    // Extract email from user object
    const email = user.email || user.userPrincipalName;
    
    if (!email) {
      console.error('No email found in user object:', user);
      return;
    }
    
    console.log('Logging in user with Azure AD ID token:', user);
    
    // Use the Azure AD ID token directly - this is a proper JWT signed by Microsoft
    if (user.id_token) {
      console.log('Using Azure AD ID token for authentication');
      login(email, user.id_token);
    } else {
      console.error('No Azure AD ID token received from authentication flow');
      throw new Error('Authentication failed: No valid token received from Azure AD');
    }
  };

  return (
    <Routes>
      {/* Auth callback route - accessible without authentication */}
      <Route 
        path="/auth/callback" 
        element={<AuthCallback onAuthSuccess={handleAzureADSuccess} />} 
      />
      
      {/* Protected routes */}
      {!isAuthenticated ? (
        <Route path="*" element={<Login />} />
      ) : (
        <>
          <Route path="/" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <Home />
              </div>
              <Footer />
            </>
          } />
          <Route path="/dashboard" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <Dashboard />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <EventsList />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events/:eventId" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <EventDetails />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events/:eventId/edit" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <EditEvent />
              </div>
              <Footer />
            </>
          } />
          <Route path="/teams" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <TeamsList />
              </div>
              <Footer />
            </>
          } />
          <Route path="/teams/:teamId" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <TeamDetails />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events/:eventId/discover" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <TeamDiscoveryPage />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events/:eventId/register" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <EventRegistrationPage />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events/:eventId/participants" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <ParticipantList />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events/:eventId/presentation" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <EventPresentationView />
              </div>
              <Footer />
            </>
          } />
          <Route path="/events/:eventId/aws-accounts" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <AwsAccountManagement />
              </div>
              <Footer />
            </>
          } />
          <Route path="/create-event" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <CreateEvent />
              </div>
              <Footer />
            </>
          } />
          <Route path="/create-team" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <CreateTeam />
              </div>
              <Footer />
            </>
          } />
          <Route path="/join-event" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <JoinEvent />
              </div>
              <Footer />
            </>
          } />
          <Route path="/join-event/:eventId" element={
            <>
              <Navigation />
              <NotificationBanner />
              <div className="container">
                <JoinEvent />
              </div>
              <Footer />
            </>
          } />
        </>
      )}
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <FeatureFlagProvider>
        <Router>
          <AppContent />
        </Router>
      </FeatureFlagProvider>
    </AuthProvider>
  );
};

export default App;
